import { useEffect, useId, useState } from 'react';
import { Tablet, X, Link2, Check, RotateCcw, ShieldCheck, Clock } from 'lucide-react';
import { isBanepadCodeExpired, type BanepadDeviceRegistration } from '../../engine/banepadDevice';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import { HIT_AREA_44 } from '../ui/hitArea';

export interface BanepadPairModalProps {
  /** The court being paired (1-based). */
  court: number;
  /** Display name for the court. */
  courtName: string;
  /** The court's active registration (pending or paired), or null if none yet. */
  registration: BanepadDeviceRegistration | null;
  /** Generates a pending registration + code for this court (also regenerates an expired one). */
  onCreate: (opts?: { force?: boolean }) => Promise<unknown> | void;
  /** Unpairs/revokes the registration by id (demo reset). */
  onRevoke: (deviceId: string) => Promise<unknown> | void;
  /** Closes the modal. */
  onClose: () => void;
}

const ACCENT = '#0e7490'; // banepad teal (matches the central view's info accent)

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

/**
 * Banepad pad-pairing modal — opened from a rutenett court card. The operator
 * generates a SHORT-LIVED registration code here and reads it onto the physical
 * pad; the actual pairing happens on the pad's own route (/pad), not here. The
 * modal just shows the code (with its countdown), the paired status once the pad
 * connects, and the unpair/revoke action. All persistence/token logic lives in
 * the engine/lib layers — this component is presentation only.
 *
 * NOTE: this is DEVICE pairing, not a user/admin login. The code and demo token
 * shown here scope a pad to ONE court; they are not credentials for a person.
 */
export function BanepadPairModal({
  courtName,
  registration,
  onCreate,
  onRevoke,
  onClose,
}: BanepadPairModalProps) {
  const [busy, setBusy] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const titleId = safeId('banepad-pair-title', useId());

  const status = registration?.status ?? 'none';
  const isPaired = status === 'paired';

  // Tick once a second to drive the pending code's countdown.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // NULL MEANS "NO EXPIRY", NOT "EXPIRED IN 1970".
  //
  // A migrated club's pads arrive with `codeExpiresAt: null` — the tenant
  // migration issues a fresh code and sets no deadline. `null - nowMs` is a
  // large NEGATIVE number, so this screen declared every imported pad expired,
  // while the engine's isBanepadCodeExpired treats null as never-expiring and
  // therefore refused to regenerate: "expired" on screen, and a regenerate
  // button that does nothing. Two halves of one rule, disagreeing (#216).
  //
  // The engine's rule is the rule. This screen only asks how long is left,
  // and that question has no answer without a deadline.
  const hasDeadline = typeof registration?.codeExpiresAt === 'number';
  const remainingMs = hasDeadline ? registration!.codeExpiresAt - nowMs : 0;
  const codeExpired = registration ? isBanepadCodeExpired(registration, nowMs) : false;

  const handleCreate = async (opts?: { force?: boolean }) => {
    setBusy(true);
    try {
      await onCreate(opts);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    setBusy(true);
    try {
      if (registration) await onRevoke(registration.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    // A `Dialog` (#900), named by its own header. Escape and the backdrop close
    // it, and focus goes back to the court's «Koble pad» chip.
    <Dialog
      labelledBy={titleId}
      onClose={onClose}
      showClose={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4"
      backdropStyle={{ background: 'rgba(2,12,20,0.55)' }}
      // Capped to the screen (#1102): the header stays in view and the body
      // scrolls under it.
      className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl flex flex-col max-h-[92vh]"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
    >
      {/* Header */}
      <div
        className="shrink-0 flex items-center justify-between px-5 py-3.5"
        style={{ background: ACCENT }}
      >
        <span className="flex items-center gap-2 text-white">
          <Tablet size={15} />
          <span id={titleId} className="font-display font-bold" style={{ fontSize: 16 }}>
            Koble pad · {courtName}
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className={`${HIT_AREA_44} text-white/80 hover:text-white`}
          aria-label="Lukk"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
        {/* Status chip */}
        <div className="flex items-center gap-2">
          <span className="font-mono uppercase tracking-wider text-ink-3" style={{ fontSize: 10 }}>
            Status
          </span>
          {isPaired ? (
            <span
              className="flex items-center gap-1 font-mono font-bold uppercase tracking-wider rounded-full px-2 py-0.5"
              style={{ fontSize: 10, background: '#cffafe', color: ACCENT }}
            >
              <Check size={11} /> Pad koblet
            </span>
          ) : (
            <span
              className="font-mono font-bold uppercase tracking-wider rounded-full px-2 py-0.5"
              style={{ fontSize: 10, background: '#f1f5f9', color: '#64748b' }}
            >
              Ikke koblet
            </span>
          )}
        </div>

        {/* No registration yet → generate a code */}
        {status === 'none' && (
          <>
            <p className="text-ink-2" style={{ fontSize: 13 }}>
              Generer en registreringskode for <span className="font-semibold">{courtName}</span> og
              bruk den på det fysiske nettbrettet for å koble det til banen.
            </p>
            <button
              onClick={() => handleCreate()}
              disabled={busy}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-2.5 font-semibold disabled:opacity-60"
              style={{ background: ACCENT, color: '#ecfeff', fontSize: 14 }}
            >
              <Link2 size={15} /> Generer registreringskode
            </button>
          </>
        )}

        {/* Pending → show the code + its countdown (pairing happens on the pad) */}
        {status === 'pending' && registration && (
          <>
            <div
              className="rounded-xl p-3.5 text-center"
              style={{
                background: codeExpired ? '#f8fafc' : '#ecfeff',
                border: `1px solid ${codeExpired ? '#e2e8f0' : '#a5f3fc'}`,
              }}
            >
              <div
                className="font-mono uppercase tracking-wider mb-1"
                style={{ fontSize: 10, color: codeExpired ? '#94a3b8' : ACCENT }}
              >
                Registreringskode
              </div>
              <div
                className="font-mono font-bold tracking-[0.3em]"
                style={{ fontSize: 26, color: codeExpired ? '#cbd5e1' : '#0f172a' }}
              >
                {registration.registrationCode}
              </div>
              {codeExpired ? (
                <div
                  className="flex items-center justify-center gap-1 mt-1.5 font-mono uppercase tracking-wider"
                  style={{ fontSize: 11, color: '#dc2626' }}
                >
                  <Clock size={11} /> Koden er utløpt
                </div>
              ) : hasDeadline ? (
                <div
                  className="flex items-center justify-center gap-1 mt-1.5 font-mono tabular-nums"
                  style={{ fontSize: 11, color: '#64748b' }}
                >
                  <Clock size={11} /> Utløper om {formatCountdown(remainingMs)}
                </div>
              ) : (
                <div
                  className="flex items-center justify-center gap-1 mt-1.5 font-mono"
                  style={{ fontSize: 11, color: '#64748b' }}
                >
                  <Clock size={11} /> Koden utløper ikke
                </div>
              )}
            </div>

            {codeExpired ? (
              <>
                <p className="text-ink-2" style={{ fontSize: 13 }}>
                  Koden var kun gyldig en kort stund. Generer en ny kode for å koble paden.
                </p>
                <button
                  onClick={() => handleCreate({ force: true })}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-1.5 rounded-full py-2.5 font-semibold disabled:opacity-60"
                  style={{ background: ACCENT, color: '#ecfeff', fontSize: 14 }}
                >
                  <RotateCcw size={15} /> Generer ny kode
                </button>
              </>
            ) : (
              <>
                <p className="text-ink-2" style={{ fontSize: 13 }}>
                  Bruk denne koden på den fysiske paden for å koble den til banen.
                </p>
                {/*
                    A REGENERATE THAT IS ALWAYS AVAILABLE, not only after the
                    code expires. A migrated club's codes never expire, so
                    without this there was no way to replace one — and the
                    codes the migration issues cannot be typed on the pad,
                    which takes a four-character body (#216).
                  */}
                <button
                  onClick={() => handleCreate({ force: true })}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-1.5 rounded-full py-2 font-semibold disabled:opacity-60"
                  style={{ background: 'transparent', color: ACCENT, fontSize: 13 }}
                >
                  <RotateCcw size={14} /> Generer ny kode
                </button>
              </>
            )}
          </>
        )}

        {/* Paired → show device token + allow unpair */}
        {status === 'paired' && registration && (
          <>
            {/*
                THE «Enhetstoken (demo)» BLOCK IS GONE (#663, approved decision).

                It printed `registration.deviceToken` on the operator's screen — a
                proof-of-concept artifact of the superseded credential model. On the
                server-owned registry path it was already empty, because the API
                does not project a token to any client. More to the point: the
                operator UI should not contain a concept whose purpose is
                displaying a credential.

                What an operator needs is below — which court this pad is on, and
                how to unpair it.
              */}
            <p className="text-ink-2" style={{ fontSize: 13 }}>
              Paden er nå koblet til <span className="font-semibold">{courtName}</span>. Tokenet er
              låst til denne banen og varer til den kobles fra her.
            </p>
            <button
              onClick={handleRevoke}
              disabled={busy}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-2.5 font-semibold disabled:opacity-60"
              style={{
                background: '#fff',
                color: '#dc2626',
                border: '1px solid #fecaca',
                fontSize: 14,
              }}
            >
              <RotateCcw size={15} /> Koble fra (nullstill)
            </button>
          </>
        )}

        {/* What pairing gives the pad — corrected for #1144: it is no longer a demo token. */}
        <div className="flex items-start gap-1.5 pt-1" style={{ color: '#94a3b8' }}>
          <ShieldCheck size={13} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontSize: 11 }}>
            Dette er enhetskobling, ikke innlogging. Paden får sin egen nøkkel, som bare gjelder
            denne banen.
          </span>
        </div>
      </div>
    </Dialog>
  );
}
